import useResponsiveSidebar from '../hooks/useResponsiveSidebar';
import { ResponsiveSidebar } from './ResponsiveSidebar';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getDivisions } from '../utils/divisions';
import {
  FileText, ShieldCheck, BadgeCheck, Wrench, Handshake, Building2, LogOut,
  Home as HomeIcon, PanelLeftClose, PanelLeftOpen,
} from 'lucide-react';
import { Button } from './ui/button';
import NotificationBell from './NotificationBell';

// Admin & Compliance division — same records as the shop division, presented in
// its own workspace (mirrors FieldLayout).
export default function AdminLayout({ children, title }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const sidebar = useResponsiveSidebar('adminSidebarCollapsed');
  const { collapsed, toggleCollapsed } = sidebar;

  const divisions = getDivisions(user);
  const multiDivision = divisions.length > 1 || user?.field_locked;
  const perms = user?.effective_permissions || [];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/admin/safety-docs', icon: BadgeCheck, label: 'Safety Docs', perm: 'safety-docs' },
    { path: '/admin/compliance', icon: ShieldCheck, label: 'Compliance', perm: 'reports.compliance' },
    { path: '/admin/equipment', icon: Wrench, label: 'Equipment', perm: 'equipment' },
    { path: '/admin/vendors', icon: Handshake, label: 'Vendors', perm: 'vendors' },
    { path: '/admin/customers', icon: Building2, label: 'Customers', perm: 'customers' },
    { path: '/admin/forms', icon: FileText, label: 'Forms', perm: 'form-templates' },
  ].filter((item) => perms.includes(item.perm));

  return (
    <div className="flex min-h-screen bg-slate-50" style={{ minHeight: '100dvh' }}>
      <ResponsiveSidebar sidebar={sidebar} testid="admin-sidebar">
          <div className="p-6 border-b border-slate-800 shrink-0">
            <h1 className="text-2xl font-black tracking-tight">ShopManager<span className="text-emerald-400">247</span></h1>
            <p className="text-xs text-emerald-400/80 mt-1 uppercase tracking-wider">Admin &amp; Compliance</p>
          </div>

          <nav className="flex-1 p-4 space-y-2 overflow-y-auto" data-testid="admin-sidebar-nav">
            {multiDivision && (
              <Link
                to="/home"
                data-testid="admin-nav-home"
                className="flex items-center gap-3 px-4 py-3 rounded-md transition-colors text-slate-300 hover:bg-slate-800 hover:text-white"
              >
                <HomeIcon size={20} />
                <span className="font-medium">Home</span>
              </Link>
            )}
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = location.pathname.startsWith(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`admin-nav-${item.label.toLowerCase()}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
                    active ? 'bg-emerald-600 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <Icon size={20} />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          <div
            className="p-4 border-t border-slate-800 shrink-0"
            style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}
          >
            <div className="px-4 py-2 mb-2">
              <p className="text-sm font-semibold text-white">{user?.name}</p>
              <p className="text-xs text-slate-400 uppercase tracking-wider">{user?.role?.replace(/_/g, ' ')}</p>
            </div>
            <Button
              onClick={handleLogout}
              data-testid="admin-logout-button"
              variant="ghost"
              className="w-full justify-start text-slate-300 hover:bg-slate-800 hover:text-white"
            >
              <LogOut size={20} className="mr-3" />
              Logout
            </Button>
          </div>
      </ResponsiveSidebar>

      <main className="flex-1 overflow-auto min-w-0">
        <div className="h-16 border-b bg-white border-slate-200 flex items-center justify-between px-4 md:px-8">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={toggleCollapsed}
              data-testid="admin-sidebar-toggle"
              title={collapsed ? 'Show sidebar' : 'Hide sidebar'}
              className="p-2 rounded-md text-slate-600 hover:bg-slate-100 transition-colors"
            >
              {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
            </button>
            <h2 className="text-lg md:text-xl font-bold text-slate-900 truncate">{title || 'Admin & Compliance'}</h2>
          </div>
          <NotificationBell />
        </div>
        <div className="p-4 md:p-8">{children}</div>
      </main>
    </div>
  );
}
